import { createCipheriv, createDecipheriv, randomBytes } from 'node:crypto';

/**
 * AES-256-GCM envelope encryption for merchant storage credentials.
 *
 * Format: `v1:<iv b64>:<authTag b64>:<ciphertext b64>`. Versioned so the
 * scheme can change without a flag day — a future `v2:` reader can decrypt
 * both while old rows are re-wrapped in the background.
 *
 * The `context` argument becomes GCM's additional authenticated data, and it
 * is not optional at any call site. It binds each ciphertext to the row it
 * belongs to (`organizationId:storageConfigId:purpose`), so an attacker with
 * database write access cannot copy organization A's encrypted secret into
 * organization B's config row and have it decrypt — GCM authentication fails
 * because the AAD no longer matches. Without AAD that is a working attack
 * against a system whose whole point is tenant isolation.
 */

const VERSION = 'v1';
const IV_BYTES = 12; // 96-bit nonce, the size GCM is specified for
const KEY_BYTES = 32;

export function generateDataKey(): Buffer {
  return randomBytes(KEY_BYTES);
}

export function encryptWithKey(key: Buffer, plaintext: Buffer | string, context: string): string {
  if (key.length !== KEY_BYTES) throw new Error('Encryption key must be 32 bytes');
  if (!context) throw new Error('Encryption context is required — it binds ciphertext to its row');

  const iv = randomBytes(IV_BYTES);
  const cipher = createCipheriv('aes-256-gcm', key, iv);
  cipher.setAAD(Buffer.from(context, 'utf8'));
  const buf = typeof plaintext === 'string' ? Buffer.from(plaintext, 'utf8') : plaintext;
  const ciphertext = Buffer.concat([cipher.update(buf), cipher.final()]);
  const authTag = cipher.getAuthTag();

  return [VERSION, iv.toString('base64'), authTag.toString('base64'), ciphertext.toString('base64')].join(':');
}

export function decryptWithKey(key: Buffer, envelope: string, context: string): Buffer {
  if (key.length !== KEY_BYTES) throw new Error('Encryption key must be 32 bytes');
  if (!context) throw new Error('Encryption context is required');

  const parts = envelope.split(':');
  if (parts.length !== 4) throw new Error('Malformed credential envelope');
  const [version, ivB64, tagB64, dataB64] = parts;
  if (version !== VERSION) throw new Error(`Unsupported credential envelope version "${version}"`);

  const decipher = createDecipheriv('aes-256-gcm', key, Buffer.from(ivB64, 'base64'));
  decipher.setAAD(Buffer.from(context, 'utf8'));
  decipher.setAuthTag(Buffer.from(tagB64, 'base64'));
  // Throws on a tampered ciphertext, a wrong key, or a mismatched context —
  // all three are the same answer to the caller: you do not get plaintext.
  return Buffer.concat([decipher.update(Buffer.from(dataB64, 'base64')), decipher.final()]);
}

export function decryptToString(key: Buffer, envelope: string, context: string): string {
  return decryptWithKey(key, envelope, context).toString('utf8');
}

/**
 * The AAD string. One helper so an encrypt and its matching decrypt can never
 * drift — a mismatch here is an unreadable credential, not a silent downgrade.
 */
export function credentialContext(
  organizationId: string,
  storageConfigId: string,
  purpose: 'accessKeyId' | 'secretKey' | 'dataKey'
): string {
  return `${organizationId}:${storageConfigId}:${purpose}`;
}

/** `AKIA••••7Q2F` — the only form of an access key ID that leaves the server. */
export function maskKeyId(accessKeyId: string): string {
  if (accessKeyId.length <= 8) return '••••';
  return `${accessKeyId.slice(0, 4)}••••${accessKeyId.slice(-4)}`;
}
